import { isDevelopment } from '../environment';
import { getRetailer } from '../data/retailer';
import { getUser } from '../data/user';
import { getFeatures } from '../features/getFeatures';
import { getFromStorage, saveToStorage, } from '../preferences/localStorageWrapper';
import { getDatadogUser } from './datadogCommon';
const SAMPLE_RATE = 10;
const PREMIUM_SAMPLE_RATE = 100;
const FEATURE_CACHE_KEY = 'dd_feature_cache';
const ALLOWED_TRACING_ORIGINS = [`https://${window.location.host}`];
const getSessionReplayFeatureKey = (applicationName) => `${applicationName}_session_replay`;
const getAllSessionReplayFeatureKey = (applicationName) => `${applicationName}_all_session_replay`;
const getFeatureCache = () => getFromStorage(FEATURE_CACHE_KEY) || {};
const getFromFeatureCache = (applicationName, featureName) => {
    const features = getFeatureCache();
    return Boolean(features && features[featureName]);
};
const setFeatureForKey = (featureCache, features, key) => {
    const updatedCache = Object.assign({}, featureCache);
    if (key in features) {
        updatedCache[key] = features[key];
    }
    else if (key in updatedCache) {
        delete updatedCache[key];
    }
    return updatedCache;
};
const initFeatureCache = (applicationName) => {
    getFeatures(getSessionReplayFeatureKey(applicationName), getAllSessionReplayFeatureKey(applicationName)).then(features => {
        const featureCache = getFeatureCache();
        const sessionKey = getSessionReplayFeatureKey(applicationName);
        const sessionAllKey = getAllSessionReplayFeatureKey(applicationName);
        let updatedCache = setFeatureForKey(featureCache, features, sessionKey);
        updatedCache = setFeatureForKey(updatedCache, features, sessionAllKey);
        saveToStorage(FEATURE_CACHE_KEY, updatedCache);
    });
};
const shouldSessionReplay = (applicationName) => getFromFeatureCache(applicationName, getSessionReplayFeatureKey(applicationName));
const shouldAllSessionReplay = (applicationName) => getFromFeatureCache(applicationName, getAllSessionReplayFeatureKey(applicationName));
const ddPromise = new Promise((resolve, reject) => {
    if ('DD_RUM' in window) {
        DD_RUM.onReady(() => {
            resolve();
        });
    }
    else {
        reject(new Error('Datadog not set'));
    }
});
ddPromise.catch(() => { });
export const detectDatadogVersion = () => {
    // Detect which version of Datadog RUM is loaded by checking the version property
    if (typeof DD_RUM !== 'undefined' &&
        DD_RUM &&
        typeof DD_RUM.version === 'string') {
        const majorVersion = parseInt(DD_RUM.version.split('.')[0], 10);
        // Return the parsed version only if it's a valid number
        if (!isNaN(majorVersion)) {
            return majorVersion;
        }
    }
    // Default to v4 if we can't detect
    return 4;
};
export const getDataDogConfig = ({ applicationName, clientToken, applicationId, gatherAll, applicationVersion, }) => {
    const shouldGatherAll = gatherAll !== null && gatherAll !== void 0 ? gatherAll : shouldAllSessionReplay(applicationName);
    const datadogVersion = detectDatadogVersion();
    const useV6Api = datadogVersion >= 6;
    const baseConfig = {
        clientToken,
        applicationId,
        site: 'us5.datadoghq.com',
        service: applicationName,
        env: 'production',
        silentMultipleInit: true,
        trackViewsManually: true,
        defaultPrivacyLevel: 'mask-user-input',
        version: applicationVersion || '',
    };
    if (useV6Api) {
        // v6 API
        return Object.assign(Object.assign({}, baseConfig), { sessionSampleRate: shouldGatherAll ? 100 : SAMPLE_RATE, sessionReplaySampleRate: shouldGatherAll ? 100 : PREMIUM_SAMPLE_RATE, trackUserInteractions: true, allowedTracingUrls: ALLOWED_TRACING_ORIGINS });
    }
    // v4 API
    return Object.assign(Object.assign({}, baseConfig), { sampleRate: shouldGatherAll ? 100 : SAMPLE_RATE, premiumSampleRate: shouldGatherAll ? 100 : PREMIUM_SAMPLE_RATE, trackInteractions: true, trackFrustrations: true, allowedTracingOrigins: ALLOWED_TRACING_ORIGINS }); // Need to force type for now when version 4 is detected as we use version 6 for types
};
const runDatadog = ({ applicationName, applicationId, clientToken, applicationVersion, }) => {
    DD_RUM.init(getDataDogConfig({
        applicationName,
        clientToken,
        applicationId,
        applicationVersion,
    }));
};
const setDatadogUser = (userPromise) => userPromise.then(([user, retailer]) => {
    DD_RUM.setUser(getDatadogUser(user, retailer));
});
/**
 * Initialise Datadog monitoring for the application.
 * See /docs/datadog for details
 */
export const initDatadog = ({ applicationName, clientToken, applicationId, authenticated = true, applicationVersion, }) => {
    if (isDevelopment(window.location.href)) {
        return;
    }
    let userPromise;
    if (authenticated) {
        initFeatureCache(applicationName);
        userPromise = Promise.all([getUser(), getRetailer()]);
    }
    ddPromise.then(() => {
        runDatadog({
            applicationName,
            clientToken,
            applicationId,
            applicationVersion,
        });
        if (userPromise) {
            setDatadogUser(userPromise);
        }
        if (shouldSessionReplay(applicationName) ||
            shouldAllSessionReplay(applicationName)) {
            DD_RUM.startSessionReplayRecording();
        }
    });
};
